export const ANOMALY_REPORT_PATH = '/report/abnormalReport';

export const ANOMALY_REPORT_FORMAT = 'html_pc';
export const ANOMALY_REPORT_COS_TYPE = 'dashboardAnomalyCos';

export interface IAnomalyReportItem {
  target_date: string;
  anomaly_count?: string | number;
  locale: string;
  format: string;
  url: string;
  original_url?: string;
  cos_key?: string;
  bytes?: string | number;
}

export type AnomalyReportUrlSigner = (data: {
  cos_type: string;
  url: string[];
}) => Promise<{ url?: string[]; }>;

export const resolveAnomalyReportLocale = (locale: string): 'zh' | 'en' =>
  locale === 'en-US' ? 'en' : 'zh';

export const normalizeAnomalyReportItemLocale = (
  locale?: string,
): 'zh' | 'en' | null => {
  const value = `${locale || ''}`.toLowerCase().replace(/_/g, '-');

  if (!value) {
    return null;
  }

  if (value === 'en' || value.startsWith('en-')) {
    return 'en';
  }

  if (value === 'zh' || value.startsWith('zh-') || value === 'cn') {
    return 'zh';
  }

  return null;
};

const isSameAnomalyReportFormat = (
  itemFormat?: string,
  targetFormat = ANOMALY_REPORT_FORMAT,
): boolean => {
  if (!itemFormat) {
    return false;
  }

  return itemFormat.toLowerCase() === targetFormat.toLowerCase();
};

export const pickAnomalyReport = (
  list: IAnomalyReportItem[] | null | undefined,
  locale: 'zh' | 'en',
  format = ANOMALY_REPORT_FORMAT,
): { url: string; targetDate: string; } | null => {
  if (!Array.isArray(list) || list.length === 0) {
    return null;
  }

  const matched = list.find(item => {
    if (!item?.url) {
      return false;
    }

    return (
      normalizeAnomalyReportItemLocale(item.locale) === locale &&
      isSameAnomalyReportFormat(item.format, format)
    );
  });
  const localeMatched = list.find(item => {
    if (!item?.url) {
      return false;
    }

    return normalizeAnomalyReportItemLocale(item.locale) === locale;
  });
  const fallback = list.find(item => item.url);
  const target = matched || localeMatched || fallback;

  if (!target?.url) {
    return null;
  }

  return {
    url: target.url,
    targetDate: target.target_date,
  };
};

export const resolveAnomalyReportTargetDate = (
  list: IAnomalyReportItem[] | null | undefined,
): string => list?.[0]?.target_date || '';

export const signAnomalyReportListUrls = async (
  list: IAnomalyReportItem[] | null | undefined,
  signer: AnomalyReportUrlSigner,
): Promise<IAnomalyReportItem[]> => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const urls = list.reduce<string[]>((acc, item) => {
    if (item?.url) {
      acc.push(item.url);
    }
    return acc;
  }, []);

  if (urls.length === 0) {
    return list;
  }

  const res = await signer({
    cos_type: ANOMALY_REPORT_COS_TYPE,
    url: urls,
  });
  const signedUrls = res?.url || [];
  let urlIndex = 0;

  return list.map(item => {
    if (!item?.url) {
      return item;
    }

    const signedUrl = signedUrls[urlIndex];
    urlIndex += 1;

    if (!signedUrl) {
      return item;
    }

    return {
      ...item,
      original_url: item.url,
      url: signedUrl,
    };
  });
};
